// src/models/ConsumableSpareLogModel.js

export class ConsumableSpareLogModel {
  static ALLOWED_KEYS = [
    "id",
    "assetId",
    "assetName",
    "assetType",
    "transactionType",
    "transactionDateTime",
    "referenceNumber",
    "remarks",
    "category",
    "unitPrice",
    "qty",
    "uom",
    "equipmentAssetId",
    "equipmentAssetName",
    "companyId",
    "locationCode",
    "createdBy",
    "createdByEmail",
    "createdDate",
  ];

  static ALLOWED_TRANSACTION_TYPES = ["Receipt", "Issue"];

  static ALLOWED_UOM = [
    "PC",
    "Nos",
    "Liters",
    "Kilograms",
    "Meters",
    "Box",
    "Set",
    "Pair",
  ];

  static ALLOWED_CATEGORIES = ["Consumables", "Spares"];

  static ALLOWED_ASSET_TYPES = ["Consumable", "Spare Part"];

  constructor(data = {}, { isCreation = false } = {}) {
    // 1. Initialize all allowed keys to null
    for (const key of ConsumableSpareLogModel.ALLOWED_KEYS) {
      this[key] = null;
    }

    if (isCreation) {
      this.id                 = data.id || this._generateId();
      this.assetId            = data.assetId            || "";
      this.assetName          = data.assetName          || "";
      this.assetType          = data.assetType          || "Consumable";
      this.transactionType    = data.transactionType    || "Receipt";
      this.transactionDateTime= data.transactionDateTime|| new Date().toISOString();
      this.referenceNumber    = data.referenceNumber    || "";
      this.remarks            = data.remarks            || "";
      this.category           = data.category           || "Consumables";

      // FIX #3: Distinguish NaN from 0 — store raw then validate
      const parsedUnitPrice = Number(data.unitPrice);
      this.unitPrice = Number.isFinite(parsedUnitPrice) ? parsedUnitPrice : 0;

      const parsedQty = Number(data.qty);
      this.qty = Number.isFinite(parsedQty) ? parsedQty : 0;

      this.uom                = data.uom                || "Nos";
      this.equipmentAssetId   = data.equipmentAssetId   || "";
      this.equipmentAssetName = data.equipmentAssetName || "";
      this.companyId          = data.companyId          || "";
      this.locationCode       = data.locationCode       || "";
      this.createdBy          = data.createdBy          || "";
      this.createdByEmail     = data.createdByEmail     || "";
      this.createdDate        = data.createdDate        || new Date().toISOString();
    } else {
      // FIX #10: Normalize types on read — Firestore may return raw values
      for (const key of ConsumableSpareLogModel.ALLOWED_KEYS) {
        if (!(key in data)) continue;
        const raw = data[key];

        if (key === "qty" || key === "unitPrice") {
          const n = Number(raw);
          this[key] = Number.isFinite(n) ? n : 0;
        } else if (key === "createdDate" || key === "transactionDateTime") {
          // Handle Firestore Timestamp objects
          if (raw && typeof raw.toDate === "function") {
            this[key] = raw.toDate().toISOString();
          } else if (raw) {
            this[key] = String(raw);
          } else {
            this[key] = null;
          }
        } else {
          this[key] = raw ?? null;
        }
      }
    }
  }

  // ── Private ────────────────────────────────────────────────────────────────

  _generateId() {
    return typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `LOG-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  // ── Validation ─────────────────────────────────────────────────────────────

  isValid() {
    if (!this.id)      return false;
    if (!this.assetId) return false;
    if (!ConsumableSpareLogModel.ALLOWED_TRANSACTION_TYPES.includes(this.transactionType))
      return false;
    if (!Number.isFinite(this.qty) || this.qty <= 0) return false;
    if (this.transactionType === "Issue" && !this.equipmentAssetId) return false;
    return true;
  }

  getValidationErrors() {
    const errors = [];

    if (!this.assetId)
      errors.push("Asset ID is required.");

    if (!ConsumableSpareLogModel.ALLOWED_TRANSACTION_TYPES.includes(this.transactionType))
      errors.push(
        `Transaction type must be one of: ${ConsumableSpareLogModel.ALLOWED_TRANSACTION_TYPES.join(", ")}.`
      );

    // FIX #3: Separate NaN from "not provided" in error message
    if (!Number.isFinite(Number(this.qty))) {
      errors.push("Quantity must be a valid number.");
    } else if (Number(this.qty) <= 0) {
      errors.push("Quantity must be greater than zero.");
    }

    if (this.transactionType === "Issue" && !this.equipmentAssetId)
      errors.push("Target equipment asset is required for Issue transactions.");

    return errors;
  }

  // ── Serialization ──────────────────────────────────────────────────────────

  toPayload() {
    const payload = {};
    for (const key of ConsumableSpareLogModel.ALLOWED_KEYS) {
      payload[key] = this[key] ?? null;
    }
    return payload;
  }

  // ── Computed helpers ───────────────────────────────────────────────────────

  isReceipt() { return this.transactionType === "Receipt"; }
  isIssue()   { return this.transactionType === "Issue";   }

  getTotalValue() {
    return (Number(this.unitPrice) || 0) * (Number(this.qty) || 0);
  }

  getFormattedQty() {
    return `${Number(this.qty || 0).toLocaleString()} ${this.uom || ""}`.trim();
  }
}